// v3 shared shapes (API-v3 3 and 4). Request bodies are zod schemas (the API
// validates with them, the web builds forms from them); responses are plain
// TypeScript types. Every v3 module file imports its building blocks from here.
//
// Import path: `@convenor/shared/v3` (the v3 names are kept out of the root
// export so they never clash with the v1/v2 shapes while both exist).
import { z } from "zod";
import {
  FILE_PURPOSES,
  NOTIFICATION_KINDS,
  ONBOARDING_STEPS,
  SETUP_STEPS,
  STEP_STATES,
  type AuditModule,
  type AuthMethod,
  type FilePurpose,
  type MfaState,
  type NotificationKind,
  type OrgRole,
  type Principal,
  type ProjectAccessRole,
  type ProjectRole,
  type Tone,
  type UserKind,
} from "../enums";
import { LIMITS } from "../limits";
import type { TrialInfo } from "../trial";
import type { ProjectPermission } from "../permissions";
import { email, isoDate, isoMonth, uuid } from "../schemas/common";

export type { ActorRef, UserRef } from "../schemas/common";
export type { AuditModule, FilePurpose, MfaState, NotificationKind, OrgRole, Principal, ProjectRole, Tone };
import type { ActorRef } from "../schemas/common";

// ---------- building blocks ----------

export type Id = string;
export const id = uuid;
export { email, isoDate, isoMonth };

/** Money: integer euro cents, 0..max. */
export const centsV3 = (max: number = LIMITS.budgetCentsMax) =>
  z.number({ invalid_type_error: "Enter an amount." }).int("Amounts are whole cents.").min(0, "The amount cannot be negative.").max(max, "The amount is too large.");

/** Days in 0.5 steps (stored as halves). */
export const days = (max: number = LIMITS.dayBudgetMax) =>
  z
    .number({ invalid_type_error: "Enter a number of days." })
    .min(0, "Cannot be negative.")
    .max(max, `Must be ${max} or less.`)
    .refine((n) => Number.isInteger(n * 2), "Use steps of half a day.");

/** A required trimmed string. */
export const text = (max: number, label = "This field") =>
  z.string().trim().min(1, `${label} is required.`).max(max, `${label} must be ${max} characters or fewer.`);
/** An optional trimmed string ("" allowed). */
export const textOrEmpty = (max: number, label = "This field") =>
  z.string().trim().max(max, `${label} must be ${max} characters or fewer.`);
/** A trimmed string or null ("" becomes null). */
export const textOrNull = (max: number, label = "This field") =>
  z
    .string()
    .trim()
    .max(max, `${label} must be ${max} characters or fewer.`)
    .nullable()
    .transform((v) => (v === "" ? null : v));

/**
 * OB-F21: a phone number: digits with spaces, "+", brackets, dashes, dots or
 * slashes, at least 6 digits ("call me" is refused). Empty = none.
 */
export const phoneOrNull = (max: number) =>
  textOrNull(max, "Phone").refine((v) => v === null || (/^\+?[\d\s().\-/]+$/.test(v) && (v.match(/\d/g) ?? []).length >= 6), "Enter a phone number, for example +389 2 123 4567.");

/** ISO-2 country code, upper-cased. */
export const country = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, "Choose a country.");

/** "HH:MM" 24-hour clock. */
export const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use the format HH:MM.");

/** ?cursor=&limit= (API-v3 1). */
export const pageQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(LIMITS.pageMax).default(LIMITS.pageDefault),
});
export type PageQuery = z.infer<typeof pageQuery>;

// ---------- common response types (API-v3 3) ----------

export type FileRef = { id: Id; name: string; contentType: string; size: number; sha256: string; url: string; createdAt: string };
export type Paged<T> = { items: T[]; nextCursor: string | null; total?: number };
export type ProjectRef = { id: Id; name: string; reference: string };
export type StatusChip = { key: string; label: string; tone: Tone };
export type Link = { label: string; path: string };
export type HistoryEntry = { id: Id; actor: ActorRef; summary: string; at: string; link: Link | null };
export type FromRecord = { text: string; link: Link | null };
export type Steps = { label: string; state: "done" | "current" | "todo" }[];
export type StepState = (typeof STEP_STATES)[number];
export { ONBOARDING_STEPS, SETUP_STEPS };

// ---------- core endpoints (API-v3 4) ----------

export type Meta = {
  appName: string;
  tagline: string;
  supportEmail: string;
  privacyUrl: string;
  termsUrl: string;
  accessibilityUrl: string;
  termsVersion: string;
  languages: { code: "en"; label: "English" }[];
  sso: { microsoft: boolean; google: boolean };
  /** The sign-in methods this deployment offers (AUTH_METHODS with their settings present); same as GET /api/auth-methods. */
  authMethods?: AuthMethod[];
  importConnectors: ("sharepoint" | "gdrive")[];
  signupAllowed: boolean;
  /** Cloudflare Turnstile site key for the sign-up forms; null = no bot check. */
  turnstileSiteKey: string | null;
  /** The public demo (DEMO_MODE): sample data, read-only, no email. */
  demoMode: boolean;
  /** Where the demo's "Start your free trial" links go; null outside the demo. */
  demoTrialUrl: string | null;
  /** The form before the sample project, when the demo's gate is on (DEMO_ENTRY_SECRET); else null. */
  demoFormUrl: string | null;
  /** The demo shows "Start free trial" (DEMO_SIGNUP_URL set): it goes to GET /api/demo/trial-link. */
  demoStartTrial: boolean;
  /** EM-N1: a test environment whose email goes nowhere (mail configuration): a banner says so, and Settings > Outbox shows the emails. */
  mailNotDelivered?: boolean;
};

export const filePurpose = z.enum(FILE_PURPOSES);
/** Multipart fields next to `file` on POST /files. */
export const uploadFields = z.object({
  purpose: filePurpose,
  projectId: uuid.optional(),
});
export type UploadFields = z.infer<typeof uploadFields>;

export const createUploadBody = z.object({
  name: z.string().trim().min(1).max(255),
  size: z.number().int().min(1),
  contentType: z.string().trim().min(1).max(200),
  purpose: z.literal("project_import"),
  projectId: uuid,
});
export type CreateUploadBody = z.infer<typeof createUploadBody>;
export type CreateUploadResult = { uploadId: Id; chunkSize: number; chunkCount: number };

export const meProjectBody = z.object({ projectId: uuid }).strict();

export type NotificationPrefs = Record<NotificationKind, { inApp: boolean; email: boolean }>;
export const notificationPrefsPatch = z
  .record(z.enum(NOTIFICATION_KINDS), z.object({ inApp: z.boolean(), email: z.boolean() }))
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.");

export type MeV3 = {
  user: {
    id: Id;
    email: string;
    name: string;
    kind: UserKind;
    emailVerified: boolean;
    hasPassword: boolean;
    ssoOnly: boolean;
    totpEnabled: boolean;
    mfaRequired: boolean;
    /** M2: two-step sign-in by a code sent by email (instead of an authenticator app). */
    twoStepEmail?: boolean;
    /** M2: false when an organisation of theirs requires an authenticator app. */
    emailCodeAllowed?: boolean;
    notificationPrefs: NotificationPrefs;
    /** Convenor staff (PLATFORM_ADMINS): may open the staff pages. */
    platformAdmin?: boolean;
  };
  session: { mfaState: MfaState; persistent: boolean; principal: Principal };
  /** `orgArea`: People and organisation (/org) is switched on for this organisation (api core/org-area.ts). */
  org: { id: Id; name: string; role: OrgRole; logoUrl: string | null; onboarded: boolean; trial: TrialInfo | null; orgArea: boolean } | null;
  orgs: { id: Id; name: string; role: OrgRole }[];
  /** withheld / deputyLead: fine-grained access on the current project (plan 2026-11-03). */
  project: (ProjectRef & { role: ProjectAccessRole; withheld: ProjectPermission[]; deputyLead: boolean }) | null;
  expertProfile: { expertId: Id; projects: ProjectRef[] } | null;
  unreadNotifications: number;
  home: string;
};

export const LOOKUP_ENTITY_TYPES = ["tor", "event", "report", "engagement", "contact", "activity", "deliverable"] as const;
export type LookupEntityType = (typeof LOOKUP_ENTITY_TYPES)[number];
export type LookupResult = { projectId: Id | null; activityId: Id | null };

export const testJobsRunBody = z.object({ kinds: z.array(z.string().max(80)).max(50).optional() });
export const testClockBody = z.object({ now: z.string().datetime({ offset: true }).nullable() });
export type TestMailbox = {
  messages: { to: string; subject: string; text: string; html: string; attachments: string[]; at: string }[];
};

export type HealthReady = { ok: boolean; db: boolean; migrations: boolean; files: boolean | null };

/** Every history panel entry may carry the module (Activity Log filter). */
export type ModuleHistoryEntry = HistoryEntry & { module: AuditModule };

/** `{ fileId }`: link an uploaded file to something. */
export const fileIdBody = z.object({ fileId: uuid });

/** A generic `{ confirm: true }` body. */
export const confirmBody = z.object({ confirm: z.literal(true) });
export const emptyBody = z.object({}).strict();

/** Tagged union of "an existing thing or a new one", used by several pickers. */
export const orgPick = z.object({ stakeholderOrgId: uuid.nullable(), newOrgName: textOrNull(LIMITS.stakeholderOrgName) });

export const monthParam = isoMonth;
export const dateParam = isoDate;
export const emailField = email;
